import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@mui/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import moment from 'moment';

import TransactionAdjustments from './TransactionAdjustments';
import { getProcessingFeesFor } from 'apis/reportAPIs';
import { feeDateWindow, formatFee, summariseFees, transactionOrderNumbers } from 'util/processingFeeUtils';

const toDate = (time) => (time && typeof time === 'object' && '_seconds' in time ? new Date(time._seconds * 1000) : time);

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
});

const paymentTime = (time) => {
  if (!time) {
    return '';
  }
  if (`${time}`.includes('/')) {
    return time;
  }
  return moment(toDate(time)).format('DD/MM/YYYY HH:mm:ss');
};

// Cash entries count `cashAmount`; card and wallet entries `paymentAmount`.
const paymentAmount = (payment) =>
  Number(payment.paymentAmount !== undefined && payment.paymentAmount !== null ? payment.paymentAmount : payment.cashAmount) || 0;

const TransactionPayments = (props) => {
  const { transaction, currencySymbol = '$' } = props;
  const classes = useStyles();
  const [fees, setFees] = useState(null);

  // Every fee record of the transaction (terminal) and of its online orders, whenever recorded:
  // refunds can come days after the payment. An older server only answers by date, so then the
  // fees from the day before payment (browser and restaurant timezones may disagree on the day)
  // up to a month after it are matched here instead.
  useEffect(() => {
    setFees(null);
    if (!transaction || !transaction.uuid) {
      return undefined;
    }
    let isCurrent = true;
    const payDay = transaction.payTime ? toDate(transaction.payTime) : new Date();
    getProcessingFeesFor({
      transactionId: transaction.uuid,
      orderNumbers: transactionOrderNumbers(transaction),
      fallbackWindow: feeDateWindow(payDay, { daysBefore: 1 }),
    })
      .then((matched) => {
        if (isCurrent) {
          setFees(summariseFees(matched));
        }
      })
      .catch((error) => {
        console.error('getProcessingFeesFor', error);
      });
    return () => {
      isCurrent = false;
    };
  }, [transaction]);

  return (
    <React.Fragment>
      <TableContainer component={Paper}>
        <Table className={classes.table} size="small" aria-label="transaction payments">
          <TableHead>
            <TableRow>
              <TableCell>Payment Type</TableCell>
              <TableCell align="right">Pay Amount</TableCell>
              <TableCell align="right">Time</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transaction && (transaction.paymentTypes || []).map((row, index) => (
              <TableRow key={`${row.type}-${index}`}>
                <TableCell component="th" scope="row">
                  {row.type}
                </TableCell>
                <TableCell align="right">{currencySymbol}{paymentAmount(row).toFixed(2)}</TableCell>
                <TableCell align="right">{paymentTime(row.time)}</TableCell>
              </TableRow>
            ))}
            {fees && fees.processingFee !== 0 && (
              <TableRow>
                <TableCell component="th" scope="row">Processing Fee</TableCell>
                <TableCell align="right">{formatFee(fees.processingFee, currencySymbol)}</TableCell>
                <TableCell align="right" />
              </TableRow>
            )}
            {fees && fees.refundFee !== 0 && (
              <TableRow>
                <TableCell component="th" scope="row">Refund Cost</TableCell>
                <TableCell align="right">{formatFee(fees.refundFee, currencySymbol)}</TableCell>
                <TableCell align="right" />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TransactionAdjustments transaction={transaction} currencySymbol={currencySymbol} />
    </React.Fragment>
  );
};

TransactionPayments.propTypes = {
  transaction: PropTypes.object,
  currencySymbol: PropTypes.string,
};

export default TransactionPayments;
